import { Download } from "lucide-react";
import type { BulkTransactionRecord, ReportFormat } from "@kkp/api-client";
import { Button } from "@kkp/ui";
import { formatCurrency, formatNumber } from "@kkp/app-kit";
import { perClientSeqNo } from "../../lib/seq";

export type BulkHistoryRowsProps = {
  transactions: BulkTransactionRecord[];
  onDownloadReport: (
    record: BulkTransactionRecord,
    format: ReportFormat,
  ) => void;
  onConfirmed?: () => void;
};

// Exhaustive status→class map over the generated BulkStatus union: a new Rust
// variant makes this object literal stop type-checking until it is handled.
const BULK_STATUS_CLASS: Record<BulkTransactionRecord["status"], string> = {
  Pending:
    "rounded-full bg-amber-500/15 px-2 py-1 text-xs font-semibold text-amber-200",
  Executed:
    "rounded-full bg-emerald-500/15 px-2 py-1 text-xs font-semibold text-emerald-200",
  Failed:
    "rounded-full bg-red-500/15 px-2 py-1 text-xs font-semibold text-red-200",
};

const bulkStatusClass = (status: BulkTransactionRecord["status"]) =>
  BULK_STATUS_CLASS[status];

export function BulkHistorySummaryCells({
  record,
  displaySeqNo,
}: {
  record: BulkTransactionRecord;
  displaySeqNo?: number;
}) {
  return (
    <>
      <td className="px-4 py-3 font-semibold text-white">
        {displaySeqNo ?? record.seqId}
      </td>
      <td className="px-4 py-3 text-white">{record.bulkTransactionId}</td>
      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
        {record.createdAt}
      </td>
      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
        {record.executedAt || "-"}
      </td>
      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
        {formatCurrency(record.totalReceivable, "id-ID", "IDR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
        {formatNumber(record.totalRecipients)}
      </td>
      <td className="px-4 py-3">
        <span className={bulkStatusClass(record.status)}>{record.status}</span>
      </td>
    </>
  );
}

export function BulkReportActions({
  record,
  onDownloadReport,
}: {
  record: BulkTransactionRecord;
  onDownloadReport: BulkHistoryRowsProps["onDownloadReport"];
}) {
  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onDownloadReport(record, "csv")}
        disabled={record.status === "Pending"}
        title={
          record.status === "Pending"
            ? "Report available after processing"
            : "Download report"
        }
      >
        <Download className="h-4 w-4" />
        CSV
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onDownloadReport(record, "pdf")}
        disabled={record.status === "Pending"}
        title={
          record.status === "Pending"
            ? "Report available after processing"
            : "Download report"
        }
      >
        <Download className="h-4 w-4" />
        PDF
      </Button>
    </>
  );
}

export function BulkHistoryRows({
  transactions,
  onDownloadReport,
}: BulkHistoryRowsProps) {
  const seqNo = perClientSeqNo(transactions.map((t) => t.seqId));
  return (
    <>
      {transactions.map((record) => (
        <tr
          key={record.bulkTransactionId}
          className="border-t border-[color:rgba(255,255,255,0.08)]"
        >
          <BulkHistorySummaryCells
            record={record}
            displaySeqNo={seqNo.get(record.seqId)}
          />
          <td className="px-4 py-3">
            <div className="flex flex-wrap gap-2">
              <BulkReportActions
                record={record}
                onDownloadReport={onDownloadReport}
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}
